import { Board, CellState, cellValueBits, withValueBits } from '@puzzlio/core';

// Cell value constants
export const FIELD1_CELL = 'f1' as const;
export const FIELD2_CELL = 'f2' as const;
export const BUSY_CELL = '0' as const;
export const EMPTY_CELL = '.' as const;

export type Field1Cell = typeof FIELD1_CELL;
export type Field2Cell = typeof FIELD2_CELL;
export type BusyCell = typeof BUSY_CELL;
export type EmptyCell = typeof EMPTY_CELL;

// Play cell types
export type CellValue = Field1Cell | Field2Cell | BusyCell | EmptyCell;

// ---------------------------------------------------------------------------
// CellValue <-> CellState bit packing
// Bits 0–1 hold the Fields CellValue; bits 2–7 hold the shared highlight state.
// ---------------------------------------------------------------------------

const BITS_TO_VALUE: CellValue[] = [EMPTY_CELL, FIELD1_CELL, FIELD2_CELL, BUSY_CELL];
const VALUE_TO_BITS: Record<CellValue, number> = {
  [EMPTY_CELL]: 0,
  [FIELD1_CELL]: 1,
  [FIELD2_CELL]: 2,
  [BUSY_CELL]: 3,
};

/** Extract the Fields CellValue (bits 0–1) from a CellState. */
export const cellValue = (cell: CellState): CellValue => BITS_TO_VALUE[cellValueBits(cell)];

/**
 * Build a fresh CellState with the given value and colour index (0 = no highlight).
 * Edge bits are always 0 for a newly created cell.
 */
export const makeState = (value: CellValue, colorIndex: number = 0): CellState =>
  VALUE_TO_BITS[value] | ((colorIndex & 0x03) << 2);

/** Replace only the CellValue bits (0–1) while preserving all other bits (colour + edges). */
export const setCellValue = (state: CellState, value: CellValue): CellState =>
  withValueBits(state, VALUE_TO_BITS[value]);

export type ValidNumberOfCells =
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14
  | 15
  | 16
  | 17
  | 18
  | 19
  | 20;
export type FieldCell = `${Field1Cell},${ValidNumberOfCells}` | `${Field2Cell},${ValidNumberOfCells}` | EmptyCell;

export type SolutionCell = Field1Cell | Field2Cell | BusyCell;

export interface FieldsBoard extends Board {
  fields: FieldCell[][];
  solution: SolutionCell[][];
}
